import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient } from '../api'
import type { Component, ComponentCreate, ComponentUpdate } from '../types'

// Components query/mutation hooks. Same pattern as useActivities:
// list query keyed on eventId; mutations write back into the cache so the
// UI reacts without a follow-up GET.

export function useComponents(eventId: number) {
  return useQuery({
    queryKey: ['components', eventId],
    queryFn: () => ApiClient.components(eventId),
    enabled: Number.isFinite(eventId) && eventId > 0
  })
}

// Append the new component to the cached list. POST returns the canonical
// wire shape so this is a drop-in append; we don't bother resorting because
// the ComponentsTab groups by category and sorts within each group when it
// renders. (Server orders by (category, sort_order, id), but the immediate
// in-memory append is good enough until the next refetch.)
export function useCreateComponent(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (body: ComponentCreate) => ApiClient.createComponent(eventId, body),
    onSuccess: (created: Component) => {
      qc.setQueryData<Component[]>(['components', eventId], (old) =>
        old ? [...old, created] : [created]
      )
    }
  })
}

// Replace the matching component in the cached list. The update body is
// passed alongside the id so the mutation only needs one TanStack-mutation
// shape (object) rather than positional args.
export function useUpdateComponent(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, body }: { id: number; body: ComponentUpdate }) =>
      ApiClient.updateComponent(eventId, id, body),
    onSuccess: (updated: Component) => {
      qc.setQueryData<Component[]>(['components', eventId], (old) =>
        old ? old.map((c) => c.id === updated.id ? updated : c) : [updated]
      )
    }
  })
}

// Remove the component from the cached list. The mutationFn returns the
// deleted id (the API returns 204 with no body, so we have to plumb the id
// through ourselves) so onSuccess knows what to filter.
export function useDeleteComponent(eventId: number) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: number) =>
      ApiClient.deleteComponent(eventId, id).then(() => id),
    onSuccess: (deletedId: number) => {
      qc.setQueryData<Component[]>(['components', eventId], (old) =>
        old ? old.filter((c) => c.id !== deletedId) : []
      )
    }
  })
}
